import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AdaptedEntity,
  GenericEntityType,
  KindEnum,
  NotFoundException,
  Provider,
  ProviderFilters,
  ReturnFieldEnum,
  TemplateEntity,
  TextFieldValue,
  WbGeneralPurposeEntityPicker,
} from '../../WbGeneralPurposeEntityPicker';
import { useLatest } from 'react-use';
import { useApi } from '@mainstage/core-plugin-api';
import {
  customAlertApiRef,
  WbSafeNunjucksApi,
  wbSafeNunjucksApiRef,
} from '../../../apis';
import { areArraysEqual } from '@agilelab/plugin-wb-platform-common';
import { UserHandlerProvider } from './UserProvider/UserProvider';
import { CatalogApi, catalogApiRef } from '@mainstage/plugin-catalog-react';
import { GroupHandlerProvider } from './GroupProvider/GroupProvider';

const TEMPLATE_PATTERN = /\$?\{\{.*?\}\}/;

/** Strip scaffolder `${{ }}` syntax down to nunjucks `{{ }}`. */
function toNunjucks(value: string): string {
  return value.replace(/\$\{\{/g, '{{');
}

/** Collect all string values containing `{{ }}` or `${{ }}` from a nested structure. */
function collectTemplates(value: unknown): string[] {
  if (typeof value === 'string' && TEMPLATE_PATTERN.test(value))
    return [toNunjucks(value)];
  if (Array.isArray(value)) return value.flatMap(collectTemplates);
  if (typeof value === 'object' && value !== null)
    return Object.values(value).flatMap(collectTemplates);
  return [];
}

/** Render nunjucks templates inside a value tree; unresolved/empty results become undefined. */
function renderValue(
  value: unknown,
  render: (t: string) => string | undefined,
): unknown {
  if (typeof value === 'string' && TEMPLATE_PATTERN.test(value)) {
    const result = render(toNunjucks(value));
    return result?.trim() ? result.trim() : undefined;
  }
  if (Array.isArray(value))
    return value.map(v => renderValue(v, render)).filter(v => v !== undefined);
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, renderValue(v, render)]),
    );
  }
  return value;
}

export type RbacFilterOptions = {
  roleIds?: string[];
  scopes?: string[];
  resolveGroups?: boolean;
  pageSize?: number;
};

export type RbacHandlerFactory = (
  nunjucksApi: WbSafeNunjucksApi,
  catalogApi: CatalogApi,
  availableKinds: TemplateEntity[],
  options: RbacFilterOptions,
) => {
  template: TemplateEntity;
  providers: Map<string, Provider<GenericEntityType[], ProviderFilters>>;
};

type UsersAndGroupsPickerProps = {
  title: string;
  description?: string | undefined;
  multiSelection?: boolean;
  freeSolo?: boolean;
  id?: string;
  maxItems?: number;
  onSelect: (val: string | string[] | undefined) => void;
  value: any | undefined;
  required?: boolean;
  disabled?: boolean;
  filters?: Record<string, any>;
  defaultValue?: any[];
  checkOptionDisabled?: ((option: AdaptedEntity) => boolean) | undefined;
  allowedKinds?: AvailableEntities[];
  templateEntities?: TemplateEntity[];
  rbacFilters?: RbacFilterOptions;
  rbacHandlerFactory?: RbacHandlerFactory;
  formContext?: any;
};

export enum AvailableEntities {
  USER = KindEnum.user,
  GROUP = KindEnum.group,
}

const defaultTemplates: Record<AvailableEntities, TemplateEntity> = {
  [AvailableEntities.USER]: {
    type: KindEnum.user,
    returnField: ReturnFieldEnum.ref,
    displayField: '{{spec.profile.displayName}}',
    columns: [
      { name: 'User', value: 'name' },
      { name: 'Email', path: '{{spec.profile.email}}' },
    ],
  },
  [AvailableEntities.GROUP]: {
    type: KindEnum.group,
    returnField: ReturnFieldEnum.ref,
    displayField: '{{spec.profile.displayName}}',
    columns: [
      { name: 'Group', value: 'name' },
      { name: 'Email', path: '{{spec.profile.email}}' },
    ],
  },
};

export const userAndGroupHandler = (
  availableKinds: TemplateEntity[],
  nunjucksApi: WbSafeNunjucksApi,
  catalogApi: CatalogApi,
) =>
  new Map<string, Provider<GenericEntityType[], ProviderFilters>>([
    [
      KindEnum.user,
      new UserHandlerProvider(nunjucksApi, catalogApi, availableKinds),
    ],
    [
      KindEnum.group,
      new GroupHandlerProvider(nunjucksApi, catalogApi, availableKinds),
    ],
  ]);

export const UsersAndGroupsPicker = ({
  title = 'Users and Groups',
  description,
  required,
  disabled,
  freeSolo = false,
  onSelect,
  value,
  defaultValue,
  multiSelection,
  maxItems,
  checkOptionDisabled,
  allowedKinds = [AvailableEntities.USER, AvailableEntities.GROUP],
  templateEntities,
  rbacFilters,
  rbacHandlerFactory,
  formContext,
}: UsersAndGroupsPickerProps) => {
  const onChangeRef = useLatest(onSelect);
  const alertApi = useApi(customAlertApiRef);
  const nunjucksApi = useApi(wbSafeNunjucksApiRef);
  const catalogApi = useApi(catalogApiRef);

  // --- Nunjucks template resolution for rbacFilters ---
  const rawRbacFilters = rbacFilters;
  const templates = useMemo(
    () => (rawRbacFilters ? collectTemplates(rawRbacFilters) : []),
    [rawRbacFilters],
  );

  useEffect(() => {
    if (templates.length === 0) return undefined;
    const release = nunjucksApi.load(templates);
    return release;
  }, [nunjucksApi, templates]);

  const resolvedRbacFilters = useMemo<RbacFilterOptions | undefined>(() => {
    if (!rawRbacFilters) return undefined;
    if (templates.length === 0) return rawRbacFilters;
    return renderValue(rawRbacFilters, t =>
      nunjucksApi.render(t, formContext ?? {}),
    ) as RbacFilterOptions;
  }, [rawRbacFilters, templates, nunjucksApi, formContext]);

  const isRbacMode =
    !!rbacHandlerFactory &&
    (!!resolvedRbacFilters?.roleIds?.length ||
      !!resolvedRbacFilters?.scopes?.length);

  const rbacResult = useMemo(() => {
    if (isRbacMode && resolvedRbacFilters && rbacHandlerFactory) {
      return rbacHandlerFactory(
        nunjucksApi,
        catalogApi,
        templateEntities ?? allowedKinds.map(kind => defaultTemplates[kind]),
        resolvedRbacFilters,
      );
    }
    return undefined;
  }, [
    isRbacMode,
    resolvedRbacFilters,
    rbacHandlerFactory,
    nunjucksApi,
    catalogApi,
    templateEntities,
    allowedKinds,
  ]);

  const availableKindsResolved: TemplateEntity[] = useMemo(() => {
    if (rbacResult) return [rbacResult.template];
    if (templateEntities) return templateEntities;
    return allowedKinds.map(kind => defaultTemplates[kind]);
  }, [rbacResult, allowedKinds, templateEntities]);

  const providerMapper = useMemo(() => {
    if (rbacResult) {
      return rbacResult.providers;
    }
    return userAndGroupHandler(availableKindsResolved, nunjucksApi, catalogApi);
  }, [rbacResult, availableKindsResolved, catalogApi, nunjucksApi]);
  const [currentValue, setCurrentValue] = useState<TextFieldValue[]>([]);
  const currentValueRef = useLatest(currentValue);

  const [resolvedValues, setResolvedValues] = useState<
    TextFieldValue[] | undefined
  >(undefined);
  const hasResolved = useRef(false);

  // Stable key that changes when RBAC filters change, forcing a full remount
  // of WbGeneralPurposeEntityPicker to clear all internal selection state.
  const rbacFiltersKey = JSON.stringify({
    roleIds: resolvedRbacFilters?.roleIds,
    scopes: resolvedRbacFilters?.scopes,
    resolveGroups: resolvedRbacFilters?.resolveGroups,
  });

  // Reset parent selection state synchronously during render (before the
  // child remounts via key) so the remounted child receives empty values.
  const prevRbacFiltersKeyRef = useRef(rbacFiltersKey);
  if (prevRbacFiltersKeyRef.current !== rbacFiltersKey) {
    prevRbacFiltersKeyRef.current = rbacFiltersKey;
    setCurrentValue([]);
    setResolvedValues(undefined);
    hasResolved.current = false;
  }

  // Notify the parent form after the synchronous reset above commits.
  const rbacFiltersKeyForEffect = useRef(rbacFiltersKey);
  useEffect(() => {
    if (rbacFiltersKeyForEffect.current !== rbacFiltersKey) {
      rbacFiltersKeyForEffect.current = rbacFiltersKey;
      onChangeRef.current(multiSelection ? [] : undefined);
    }
  }, [rbacFiltersKey, onChangeRef, multiSelection]);

  useEffect(() => {
    if (value === '' || value === undefined || value === null) {
      setCurrentValue([]);
      return;
    }
  }, [value]);

  const fetchData = useCallback(
    async (ref: string) => {
      for (const [key, provider] of Array.from(providerMapper.entries())) {
        if (availableKindsResolved.some(a => a.type === key)) {
          try {
            const res = await provider.resolve(ref);

            if (res) {
              return res;
            }
          } catch (err) {
            if (err instanceof NotFoundException) {
              return undefined;
            }

            alertApi.post({
              error: err,
              severity: 'error',
            });
            return undefined;
          }
        }
      }

      return undefined;
    },
    [alertApi, availableKindsResolved, providerMapper],
  );

  const loadInitialValue = useCallback(
    (initialValues: string[]) => {
      const checkCurrentValue = (val: string) => {
        const justResolved = currentValueRef.current?.find(
          cv => cv.value === val,
        );
        return justResolved ? justResolved : null;
      };

      return initialValues.map(async input => {
        const justResolved = checkCurrentValue(input);
        if (justResolved) {
          return justResolved;
        }

        const entity = await fetchData(input);
        return entity
          ? entity
          : {
              kind: KindEnum.arbitrary,
              label: input,
              value: input,
            };
      });
    },
    [currentValueRef, fetchData],
  );

  const handleSelect = useCallback(
    (values: TextFieldValue[] | undefined) => {
      if (!values && currentValueRef.current.length === 0) return;
      if (values && areArraysEqual(values, currentValueRef.current)) return;

      setCurrentValue(values ?? []);
      const formDataValues = values
        ?.map(v => v?.value)
        .filter((v): v is string => typeof v === 'string');
      onChangeRef.current(
        multiSelection ? formDataValues : formDataValues?.[0],
      );
    },
    [onChangeRef, currentValueRef, multiSelection],
  );

  useEffect(() => {
    if (hasResolved.current) return;

    let valuesFromValue: (typeof value)[] = [];
    if (value) {
      valuesFromValue = Array.isArray(value) ? value : [value];
    }

    const initialValues = [...(defaultValue ?? []), ...valuesFromValue];
    if (!initialValues.length) return;

    hasResolved.current = true;

    const resolveValues = async () => {
      const resolved = await Promise.all(loadInitialValue(initialValues));
      setResolvedValues(resolved);
    };

    resolveValues();
  }, [defaultValue, value, loadInitialValue]);

  const wrappedCheckOptionDisabled = useMemo(() => {
    if (!checkOptionDisabled) return undefined;
    const selectedValues = new Set(currentValue.map(cv => cv.value));
    return (option: AdaptedEntity) => {
      if (selectedValues.has(option.ref) || selectedValues.has(option.urn)) {
        return false;
      }
      return checkOptionDisabled(option);
    };
  }, [checkOptionDisabled, currentValue]);

  return (
    <WbGeneralPurposeEntityPicker
      key={rbacFiltersKey}
      title={title}
      description={description}
      availableKinds={availableKindsResolved}
      freeSolo={freeSolo}
      required={required}
      multiSelection={multiSelection}
      disabled={disabled}
      providerMapper={providerMapper}
      onSelect={handleSelect}
      value={currentValue ?? []}
      resolvedValues={resolvedValues}
      maxItems={maxItems}
      checkOptionDisabled={wrappedCheckOptionDisabled}
      formContext={formContext}
    />
  );
};
